import {configureStore, ThunkAction, Action, ThunkDispatch, AnyAction, Dispatch} from '@reduxjs/toolkit';
import {
  IAuthManager,
  ISecureStorage,
  ITranslationService,
  INotificationService,
  ICalendarService,
  ILocalStorage,
  IApiClient,
} from '@lib/infrastructure';
import {rootReducer, RootState} from './rootReducer';
import {timeStampMiddleware, listenerMiddleware} from './middleware';

type CreateStoreProps = {
  authManager: IAuthManager;
  apiClient: IApiClient;
  localStore: ILocalStorage;
  secureStore: ISecureStorage;
  i18n: ITranslationService;
  notifications: INotificationService<AppDispatch>;
  calendarService: ICalendarService;
};

export const createStore = ({
  authManager,
  apiClient,
  localStore,
  secureStore,
  i18n,
  notifications,
  calendarService,
}: CreateStoreProps) => {
  if (!authManager || !apiClient || !localStore || !secureStore || !i18n || !notifications || !calendarService) {
    throw Error('Please specify the required dependencies');
  }

  const extraArgs: ExtraArgs = {
    authManager: authManager,
    apiClient: apiClient,
    localStore: localStore,
    secureStore: secureStore,
    i18n: i18n,
    notifications: notifications,
    calendarService: calendarService,
  };

  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: {
          extraArgument: extraArgs,
        },
        serializableCheck: {
          ignoredActions: [
            'workspaceReservation/createGroupWorkspaceReservation/rejected',
            'ui/workspaceReservation/saveGroupWorkspaceReservation/rejected',
            'workspaceReservations/updateWorkspaceReservation/rejected',
            'ui/workspaceReservation/saveWorkspaceReservation/rejected',
          ],
        },
        //immutableCheck: true,
        //serializableCheck: true,
      })
        .prepend(listenerMiddleware.middleware)
        .concat(timeStampMiddleware),
  });
};

export type ExtraArgs = {
  authManager: IAuthManager;
  apiClient: IApiClient;
  localStore: ILocalStorage;
  secureStore: ISecureStorage;
  i18n: ITranslationService;
  notifications: INotificationService<AppDispatch>;
  calendarService: ICalendarService;
};
export type AppDispatch = ThunkDispatch<RootState, ExtraArgs, AnyAction> & Dispatch<AnyAction>;
export type AppThunk<T = void> = ThunkAction<T, RootState, ExtraArgs, Action<any>>;
export type AppThunkApiConfig<TRejectedValue = unknown> = {
  state: RootState;
  dispatch: AppDispatch;
  extra: ExtraArgs;
  rejectValue: TRejectedValue;
};
export type AppStore = ReturnType<typeof createStore>;
